/**
 * Three.js pile of booster packs. Covers stay readable; the top pack
 * lifts, stands, and the crimped top is torn off (not hinged).
 */

import * as THREE from 'three'
import { CARD_BACK_URL } from '../../utils/cardImages'
import {
  applyWebglBudget,
  listenWebglLost,
  webglRendererOptions,
} from '../webglPhone'
import { fitFov, packFrame, type PackFrame } from './packFrame'
import './PackStack.css'

export type PackStackMode = 'draw' | 'idle' | 'hold'

export interface PackStackCallbacks {
  onLift?: () => void
  onOpen?: () => void
  /** The GPU dropped the canvas. The opener should continue in the DOM. */
  onContextLost?: () => void
}

interface Sparkle {
  sprite: THREE.Sprite
  origin: THREE.Vector3
  dir: THREE.Vector3
  speed: number
  born: number
  life: number
}

interface StackPack {
  group: THREE.Group
  flap: THREE.Group
  cards: THREE.Mesh[]
  restPos: THREE.Vector3
  restEuler: THREE.Euler
  isTop: boolean
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t
const clamp01 = (t: number): number => Math.max(0, Math.min(1, t))
const easeOut = (t: number): number => 1 - (1 - t) ** 3
const easeInOut = (t: number): number =>
  t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2

const bezier = (a: number, b: number, c: number, t: number): number => {
  const u = 1 - t
  return u * u * a + 2 * u * t * b + t * t * c
}

const hash = (i: number): number => {
  const x = Math.sin(i * 127.1 + 311.7) * 43758.5453
  return x - Math.floor(x)
}

const PACK_W = 1.02
const PACK_H = 1.48
const PACK_D = 0.04
const FLAP_H = 0.2
const BODY_H = PACK_H - FLAP_H
const STACK_GAP = 0.052
const CARD_W = 0.5
const CARD_H = 0.74
const LOCAL_COVER = '/assets/packs/lob.jpg'

export class PackStack {
  private readonly root: HTMLDivElement
  private readonly canvas: HTMLCanvasElement
  private readonly renderer: THREE.WebGLRenderer
  private readonly scene: THREE.Scene
  private readonly camera: THREE.PerspectiveCamera
  private readonly disposables: { dispose: () => void }[] = []
  private readonly packs: StackPack[] = []
  private readonly sparkles: Sparkle[] = []
  private readonly callbacks: PackStackCallbacks
  private readonly mode: PackStackMode
  private readonly resizeObs: ResizeObserver
  private readonly completeAt = 3.15
  private raf = 0
  private unlistenGl: (() => void) | null = null
  private contextLost = false
  private startedAt = 0
  private skipped = false
  private disposed = false
  private finished = false
  private liftPlayed = false
  private openPlayed = false
  private tearAmount = 0
  private holdRipping = false
  private ripFrom = 0
  private ripStartedAt = 0
  private sparkTex: THREE.CanvasTexture | null = null
  private texturesReady: Promise<void>
  private resolvePlay: (() => void) | null = null
  private readonly cardClipPlane = new THREE.Plane()
  private readonly clipNormal = new THREE.Vector3()
  private readonly clipPoint = new THREE.Vector3()
  private frame: PackFrame = 'wide'

  constructor(
    host: HTMLElement,
    coverUrl: string,
    count: number,
    mode: PackStackMode,
    callbacks: PackStackCallbacks = {},
    labelText = '',
  ) {
    this.callbacks = callbacks
    this.mode = mode

    this.root = document.createElement('div')
    this.root.className = 'ps-root'
    this.canvas = document.createElement('canvas')
    this.canvas.className = 'ps-canvas'
    this.root.append(this.canvas)
    if (labelText) {
      const label = document.createElement('p')
      label.className = 'ps-label'
      label.textContent = labelText
      this.root.append(label)
    }
    try {
      this.renderer = new THREE.WebGLRenderer(webglRendererOptions(this.canvas))
    } catch (err) {
      this.canvas.remove()
      throw err
    }
    applyWebglBudget(this.renderer, 2)
    this.renderer.localClippingEnabled = true
    this.unlistenGl = listenWebglLost(this.canvas, this.handleContextLost)

    this.scene = new THREE.Scene()
    this.camera = new THREE.PerspectiveCamera(40, 1, 0.1, 40)
    this.placeCamera(0)

    this.addLights()
    this.addFloor()
    this.resizeObs = new ResizeObserver(() => this.resize())

    try {
      host.appendChild(this.root)
      this.refreshFrame()
      this.texturesReady = this.buildPacks(coverUrl, Math.max(1, count))
      this.resize()
      this.resizeObs.observe(this.root)
    } catch (err) {
      this.unlistenGl()
      this.unlistenGl = null
      this.resizeObs.disconnect()
      this.root.remove()
      throw err
    }
  }

  private handleContextLost = (): void => {
    if (this.disposed || this.contextLost) return
    this.contextLost = true
    if (this.raf) cancelAnimationFrame(this.raf)
    this.raf = 0
    this.callbacks.onContextLost?.()
  }

  play(): Promise<void> {
    return new Promise((resolve) => {
      if (this.disposed) {
        resolve()
        return
      }
      this.resolvePlay = resolve
      const start = () => {
        if (this.disposed) {
          resolve()
          return
        }
        this.startedAt = performance.now()
        this.tick()
      }
      void this.texturesReady.then(start).catch(start)
    })
  }

  skip(): void {
    if (this.disposed || this.finished) return
    this.skipped = true
  }

  /**
   * Hold mode: 0 = sealed and still, 1 = flap fully peeled.
   */
  setTear(amount: number): void {
    if (this.mode !== 'hold' || this.holdRipping) return
    this.tearAmount = clamp01(amount)
  }

  /** Hold mode: finish the peel, sparkle, then keep posing until disposed. */
  rip(): void {
    if (this.mode !== 'hold' || this.disposed || this.holdRipping) return
    this.holdRipping = true
    this.ripFrom = this.tearAmount
    this.ripStartedAt = performance.now()
  }

  dispose(): void {
    this.disposed = true
    if (this.raf) cancelAnimationFrame(this.raf)
    this.unlistenGl?.()
    this.unlistenGl = null
    this.resizeObs.disconnect()
    this.scene.traverse((obj) => {
      const mesh = obj as THREE.Mesh
      if (mesh.isMesh) {
        mesh.geometry?.dispose()
        const mats = Array.isArray(mesh.material)
          ? mesh.material
          : [mesh.material]
        for (const mat of mats) mat?.dispose()
      }
    })
    for (const item of this.disposables) item.dispose()
    this.renderer.dispose()
    this.root.remove()
    this.resolvePlay?.()
    this.resolvePlay = null
  }

  private addLights(): void {
    const ambient = new THREE.AmbientLight(0xffffff, 0.85)
    const key = new THREE.DirectionalLight(0xfff6e0, 1.7)
    key.position.set(1.8, 5.8, 4.2)
    key.castShadow = true
    key.shadow.mapSize.set(1024, 1024)
    const fill = new THREE.PointLight(0xffffff, 1.25, 16)
    fill.position.set(0, 2.6, 3.8)
    const rim = new THREE.DirectionalLight(0xfde68a, 0.7)
    rim.position.set(-3.4, 2.6, -1.2)
    this.scene.add(ambient, key, fill, rim)
  }

  private addFloor(): void {
    const floor = new THREE.Mesh(
      new THREE.CircleGeometry(3.6, 40),
      new THREE.ShadowMaterial({ opacity: 0.4 }),
    )
    floor.rotation.x = -Math.PI / 2
    floor.position.y = -0.02
    floor.receiveShadow = true
    this.scene.add(floor)
  }

  private track<T extends { dispose: () => void }>(item: T): T {
    this.disposables.push(item)
    return item
  }

  private makeSparkTex(): THREE.CanvasTexture {
    const c = document.createElement('canvas')
    c.width = c.height = 64
    const ctx = c.getContext('2d')
    if (ctx) {
      const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32)
      g.addColorStop(0, 'rgba(255,255,255,1)')
      g.addColorStop(0.22, 'rgba(253,224,71,0.95)')
      g.addColorStop(0.55, 'rgba(250,204,21,0.35)')
      g.addColorStop(1, 'rgba(250,204,21,0)')
      ctx.fillStyle = g
      ctx.fillRect(0, 0, 64, 64)
    }
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    this.sparkTex = tex
    return this.track(tex)
  }

  private spawnSparkles(pack: StackPack): void {
    const tex = this.sparkTex ?? this.makeSparkTex()
    const mouth = new THREE.Vector3(0, PACK_H / 2 - 0.04, PACK_D / 2)
    for (let i = 0; i < 18; i++) {
      const mat = new THREE.SpriteMaterial({
        map: tex,
        color: i % 3 === 0 ? 0xffffff : i % 3 === 1 ? 0xfde68a : 0xfbbf24,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      })
      this.track(mat)
      const sprite = new THREE.Sprite(mat)
      sprite.visible = false
      sprite.scale.setScalar(0.04)
      sprite.position.copy(mouth)
      pack.group.add(sprite)
      const angle = (i / 18) * Math.PI * 2 + hash(i) * 0.4
      const up = 0.35 + hash(i + 5) * 0.45
      this.sparkles.push({
        sprite,
        origin: mouth.clone(),
        dir: new THREE.Vector3(
          Math.cos(angle) * (0.18 + hash(i + 9) * 0.28),
          up,
          Math.sin(angle) * 0.14 + 0.08,
        ),
        speed: 0.45 + hash(i + 13) * 0.28,
        born: -1,
        life: 0.55 + hash(i + 17) * 0.22,
      })
    }
  }

  private igniteSparkles(t: number): void {
    for (const spark of this.sparkles) {
      spark.born = t
      spark.sprite.visible = true
      const mat = spark.sprite.material as THREE.SpriteMaterial
      mat.opacity = 0
      spark.sprite.position.copy(spark.origin)
    }
  }

  private tickSparkles(t: number): void {
    for (const spark of this.sparkles) {
      if (spark.born < 0) continue
      const age = t - spark.born
      const u = clamp01(age / spark.life)
      spark.sprite.position.copy(spark.origin).addScaledVector(
        spark.dir,
        spark.speed * age,
      )
      const fade = u < 0.22 ? u / 0.22 : 1 - (u - 0.22) / 0.78
      const mat = spark.sprite.material as THREE.SpriteMaterial
      mat.opacity = fade * 0.7
      spark.sprite.scale.setScalar(0.04 + fade * 0.09)
      spark.sprite.visible = u < 1
    }
  }

  private coverCandidates(url: string): string[] {
    return [url, LOCAL_COVER].filter(
      (src, i, all) => all.indexOf(src) === i,
    )
  }

  private loadImageTex(url: string): Promise<THREE.Texture> {
    return new Promise((resolve, reject) => {
      const img = new Image()
      if (/^https?:/i.test(url)) img.crossOrigin = 'anonymous'
      img.onload = () => {
        const tex = new THREE.Texture(img)
        tex.colorSpace = THREE.SRGBColorSpace
        tex.anisotropy = this.renderer.capabilities.getMaxAnisotropy()
        tex.needsUpdate = true
        this.track(tex)
        resolve(tex)
      }
      img.onerror = () => reject(new Error(url))
      img.src = url
    })
  }

  private async loadCover(url: string): Promise<THREE.Texture> {
    for (const src of this.coverCandidates(url)) {
      try {
        return await this.loadImageTex(src)
      } catch {
        /* try next */
      }
    }
    return this.makeBoosterFallback()
  }

  /** Painted booster so a failed load still looks like a pack, not a brick. */
  private makeBoosterFallback(): THREE.CanvasTexture {
    const c = document.createElement('canvas')
    c.width = 512
    c.height = 740
    const ctx = c.getContext('2d')
    if (!ctx) return this.track(new THREE.CanvasTexture(c))
    const g = ctx.createLinearGradient(0, 0, 0, 740)
    g.addColorStop(0, '#1e3a8a')
    g.addColorStop(1, '#0f172a')
    ctx.fillStyle = g
    ctx.fillRect(0, 0, 512, 740)
    ctx.strokeStyle = '#e8c547'
    ctx.lineWidth = 18
    ctx.strokeRect(16, 16, 480, 708)
    ctx.fillStyle = '#e8c547'
    ctx.font = 'bold 36px serif'
    ctx.textAlign = 'center'
    ctx.fillText('YU-GI-OH!', 256, 80)
    ctx.font = 'bold 22px serif'
    ctx.fillText('BOOSTER', 256, 120)
    ctx.fillStyle = '#93c5fd'
    ctx.fillRect(56, 160, 400, 420)
    ctx.fillStyle = '#e8c547'
    ctx.fillRect(0, 620, 512, 28)
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    return this.track(tex)
  }

  private sliceTex(
    src: THREE.Texture,
    yOffset: number,
    yRepeat: number,
  ): THREE.Texture {
    const tex = src.clone()
    tex.repeat.set(1, yRepeat)
    tex.offset.set(0, yOffset)
    tex.needsUpdate = true
    return this.track(tex)
  }

  private foilMat(tex: THREE.Texture): THREE.MeshStandardMaterial {
    return this.track(
      new THREE.MeshStandardMaterial({
        map: tex,
        emissive: new THREE.Color(0xffffff),
        emissiveMap: tex,
        emissiveIntensity: 0.42,
        roughness: 0.3,
        metalness: 0.06,
      }),
    )
  }

  private sideMat(color: number, metal = 0.45): THREE.MeshStandardMaterial {
    return this.track(
      new THREE.MeshStandardMaterial({
        color,
        roughness: 0.38,
        metalness: metal,
      }),
    )
  }

  private buildBooster(
    bodyMats: THREE.Material[],
    flapMats: THREE.Material[],
    innerMat: THREE.Material,
    cardMat: THREE.Material,
  ): { group: THREE.Group; flap: THREE.Group; cards: THREE.Mesh[] } {
    const group = new THREE.Group()
    const bodyGeo = this.track(new THREE.BoxGeometry(PACK_W, BODY_H, PACK_D))
    const body = new THREE.Mesh(bodyGeo, bodyMats)
    body.position.y = -FLAP_H / 2
    body.castShadow = true
    body.receiveShadow = true
    group.add(body)

    const inner = new THREE.Mesh(
      this.track(new THREE.PlaneGeometry(PACK_W * 0.88, FLAP_H * 0.7)),
      innerMat,
    )
    inner.position.set(0, PACK_H / 2 - FLAP_H * 0.35, 0)
    group.add(inner)

    const cutRim = new THREE.Mesh(
      this.track(new THREE.BoxGeometry(PACK_W + 0.01, 0.016, PACK_D + 0.012)),
      this.sideMat(0xc4a035, 0.7),
    )
    cutRim.position.set(0, PACK_H / 2 - FLAP_H + 0.008, 0)
    group.add(cutRim)

    const cards: THREE.Mesh[] = []
    const cardGeo = this.track(new THREE.PlaneGeometry(CARD_W, CARD_H))
    for (let i = 0; i < 3; i++) {
      const card = new THREE.Mesh(cardGeo, cardMat)
      card.castShadow = true
      card.visible = false
      group.add(card)
      cards.push(card)
    }

    const flap = new THREE.Group()
    flap.position.set(0, PACK_H / 2 - FLAP_H, 0)
    const flapGeo = this.track(new THREE.BoxGeometry(PACK_W, FLAP_H, PACK_D))
    const flapMesh = new THREE.Mesh(flapGeo, flapMats)
    flapMesh.position.y = FLAP_H / 2
    flapMesh.castShadow = true
    flap.add(flapMesh)

    const crimp = new THREE.Mesh(
      this.track(new THREE.BoxGeometry(PACK_W + 0.03, 0.04, PACK_D + 0.018)),
      this.sideMat(0xe8c547, 0.85),
    )
    crimp.position.set(0, FLAP_H - 0.012, 0)
    flap.add(crimp)

    const strip = new THREE.Mesh(
      this.track(new THREE.BoxGeometry(PACK_W * 0.94, 0.016, 0.012)),
      this.sideMat(0xfde68a, 0.9),
    )
    strip.position.set(0, 0.01, PACK_D / 2 + 0.004)
    flap.add(strip)

    group.add(flap)
    return { group, flap, cards }
  }

  private async buildPacks(coverUrl: string, count: number): Promise<void> {
    const frontTex = await this.loadCover(coverUrl)
    const split = BODY_H / PACK_H
    const bodyTex = this.sliceTex(frontTex, 0, split)
    const flapTex = this.sliceTex(frontTex, split, 1 - split)
    const bodyFront = this.foilMat(bodyTex)
    const flapFront = this.foilMat(flapTex)
    const back = this.sideMat(0x1e3a5f, 0.15)
    const gold = this.sideMat(0xd4af37, 0.72)
    const cardboard = this.sideMat(0x8a6a1f, 0.2)

    // Box faces: +x -x +y -y +z -z
    const bodyMats = [gold, gold, cardboard, cardboard, bodyFront, back]
    const flapMats = [gold, gold, cardboard, cardboard, flapFront, back]

    let cardTex: THREE.Texture
    try {
      cardTex = await this.loadImageTex(CARD_BACK_URL)
    } catch {
      cardTex = this.makeBoosterFallback()
    }
    const cardMat = this.foilMat(cardTex)
    cardMat.emissiveIntensity = 0.25
    cardMat.side = THREE.DoubleSide
    cardMat.clippingPlanes = [this.cardClipPlane]
    cardMat.clipShadows = true
    const innerMat = this.sideMat(0x111827, 0.05)

    for (let i = 0; i < count; i++) {
      const { group, flap, cards } = this.buildBooster(
        bodyMats,
        flapMats,
        innerMat,
        cardMat,
      )
      const isHoldFront = this.mode === 'hold' && i === count - 1
      const restPos = this.packRestPos(i, count, isHoldFront)
      const restEuler = this.packRestEuler(i, isHoldFront)
      group.position.copy(restPos)
      group.rotation.copy(restEuler)
      this.scene.add(group)
      this.packs.push({
        group,
        flap,
        cards,
        restPos,
        restEuler,
        isTop: i === count - 1,
      })
    }
    const top = this.packs[this.packs.length - 1]
    if ((this.mode === 'draw' || this.mode === 'hold') && top) {
      this.spawnSparkles(top)
    }
  }

  private refreshFrame(): void {
    this.frame = packFrame(this.root.clientWidth, this.root.clientHeight)
  }

  private holdScale(): number {
    if (this.frame === 'portrait') return 1.06
    if (this.frame === 'short') return 0.92
    return 1.14
  }

  /** Standing pack the player tears, in front of the leftover pile. */
  private frontPos(): THREE.Vector3 {
    if (this.frame === 'portrait') return new THREE.Vector3(0, 0.46, 1.02)
    if (this.frame === 'short') return new THREE.Vector3(0, 0.3, 1.18)
    return new THREE.Vector3(0, 0.4, 1.68)
  }

  private packRestPos(
    i: number,
    _count: number,
    isHoldFront: boolean,
  ): THREE.Vector3 {
    if (isHoldFront) return this.frontPos()
    const hold = this.mode === 'hold'
    // Compact: leftover packs sit behind the tear, not off to a side, so
    // the booster stays centered and cards do not clip on the right.
    const pileX = !hold ? 0 : this.frame === 'wide' ? -1.32 : 0
    const pileZ = !hold
      ? (hash(i + 3) - 0.5) * 0.1
      : this.frame === 'portrait'
        ? -0.22 + (hash(i + 3) - 0.5) * 0.04
        : this.frame === 'short'
          ? -0.12
          : 0.22 + (hash(i + 3) - 0.5) * 0.1
    return new THREE.Vector3(
      (hash(i) - 0.5) * 0.1 + pileX,
      i * STACK_GAP,
      pileZ,
    )
  }

  private packRestEuler(i: number, isHoldFront: boolean): THREE.Euler {
    if (isHoldFront) return new THREE.Euler(-0.08, 0, 0)
    return new THREE.Euler(
      -Math.PI / 2,
      (hash(i + 7) - 0.5) * 0.16,
      (hash(i + 11) - 0.5) * 0.05,
    )
  }

  private relayoutPacks(): void {
    const n = this.packs.length
    for (let i = 0; i < n; i++) {
      const pack = this.packs[i]
      if (!pack) continue
      const isHoldFront = this.mode === 'hold' && pack.isTop
      pack.restPos.copy(this.packRestPos(i, n, isHoldFront))
      pack.restEuler.copy(this.packRestEuler(i, isHoldFront))
      const posedTop =
        pack.isTop && (this.mode === 'draw' || this.mode === 'hold')
      if (!posedTop || this.mode === 'hold') {
        pack.group.position.copy(pack.restPos)
        pack.group.rotation.copy(pack.restEuler)
      }
    }
  }

  private placeCamera(t: number): void {
    const idle = this.mode === 'idle'
    const hold = this.mode === 'hold'
    const frame = this.frame
    let radius = idle ? 3.15 : 4.4
    let height = idle ? 2.35 : 2.2
    let lookY = idle ? 0.22 : 0.78
    let lookZ = idle ? 0 : 0.45
    let yawBase = frame === 'wide' ? -0.38 : 0
    let fitW = idle ? 1.6 : 2.15
    let fitH = idle ? 1.35 : 2.45

    if (!idle && frame === 'portrait') {
      radius = 4.05
      height = 1.48
      lookY = 0.5
      lookZ = 1.02
      yawBase = 0
      fitW = 1.95
      fitH = 2.55
    } else if (!idle && frame === 'short') {
      radius = 4.55
      height = 1.42
      lookY = 0.4
      lookZ = 1.18
      yawBase = 0
      fitW = 2.45
      fitH = 1.9
    } else if (idle && frame === 'portrait') {
      radius = 3.4
      yawBase = 0
      lookZ = 0
    }

    const yaw =
      yawBase + (hold ? 0 : Math.sin(t * 0.4) * (idle ? 0.08 : 0.07))
    this.camera.position.set(
      Math.sin(yaw) * radius,
      height,
      Math.cos(yaw) * radius,
    )
    const look = new THREE.Vector3(0, lookY, lookZ)
    this.camera.lookAt(look)
    const dist = this.camera.position.distanceTo(look)
    const aspect = Math.max(0.2, this.camera.aspect)
    this.camera.fov = fitFov(aspect, dist, fitW, fitH)
    this.camera.updateProjectionMatrix()
  }

  private resize(): void {
    const w = Math.max(1, this.root.clientWidth)
    const h = Math.max(1, this.root.clientHeight)
    const prev = this.frame
    this.refreshFrame()
    this.camera.aspect = w / h
    if (this.packs.length > 0 && prev !== this.frame) this.relayoutPacks()
    this.placeCamera(0)
    this.renderer.setSize(w, h, false)
  }

  private poseTop(pack: StackPack, t: number): void {
    const liftT = easeOut(clamp01((t - 0.32) / 0.48))
    const flyT = easeInOut(clamp01((t - 0.78) / 0.85))
    const standT = easeOut(clamp01((t - 0.5) / 0.65))
    const openT = easeOut(clamp01((t - 1.25) / 0.7))

    if (liftT > 0.08 && !this.liftPlayed) {
      this.liftPlayed = true
      this.callbacks.onLift?.()
    }

    if (openT > 0.08 && !this.openPlayed) {
      this.openPlayed = true
      this.callbacks.onOpen?.()
      this.igniteSparkles(t)
    }

    const lift = pack.restPos.clone().add(new THREE.Vector3(0, 0.85, 0.16))
    const end = this.frontPos()
    const midX = this.frame === 'wide' ? 0.12 : 0
    const p = flyT > 0
      ? new THREE.Vector3(
          bezier(lift.x, midX, end.x, flyT),
          bezier(lift.y, 1.28, end.y, flyT),
          bezier(lift.z, 1.05, end.z, flyT),
        )
      : new THREE.Vector3(
          lerp(pack.restPos.x, lift.x, liftT),
          lerp(pack.restPos.y, lift.y, liftT),
          lerp(pack.restPos.z, lift.z, liftT),
        )
    pack.group.position.copy(p)
    pack.group.rotation.set(
      lerp(pack.restEuler.x, -0.08, standT),
      lerp(pack.restEuler.y, this.frame === 'wide' ? 0.1 : 0, standT),
      lerp(pack.restEuler.z, 0, standT),
    )
    pack.group.scale.setScalar(lerp(1, this.holdScale(), flyT))
    this.poseFlapTear(pack, openT)
    this.updateCardClip(pack)
    this.poseCardsOut(pack, t)
  }

  private poseCardsOut(pack: StackPack, t: number): void {
    for (let i = 0; i < pack.cards.length; i++) {
      const emerge = easeOut(clamp01((t - 1.28 - i * 0.1) / 0.85))
      this.placeEmergedCard(pack, i, emerge)
    }
  }

  private poseCardsAmount(pack: StackPack, amount: number): void {
    for (let i = 0; i < pack.cards.length; i++) {
      const emerge = easeOut(clamp01(amount - i * 0.08))
      this.placeEmergedCard(pack, i, emerge)
    }
  }

  private placeEmergedCard(
    pack: StackPack,
    i: number,
    emerge: number,
  ): void {
    const card = pack.cards[i]
    const mouthY = PACK_H / 2 - FLAP_H
    const startY = mouthY - CARD_H * 0.48
    const peek =
      this.frame === 'portrait' ? 0.16 : this.frame === 'short' ? 0.14 : 0.28
    const endY = mouthY + CARD_H * peek
    card.visible = emerge > 0.02
    if (!card.visible) return
    const fan = i - (pack.cards.length - 1) / 2
    const fanT = easeOut(clamp01((emerge - 0.5) / 0.5))
    const spread = this.frame === 'wide' ? 0.1 : 0.02
    card.position.set(
      fan * spread * fanT,
      lerp(startY, endY, emerge),
      lerp(0, PACK_D / 2 + 0.01 + i * 0.004, emerge),
    )
    card.rotation.set(0, 0, fan * 0.08 * fanT)
  }

  /** Hide the part of each card still inside the pack. */
  private updateCardClip(pack: StackPack): void {
    const mouthY = PACK_H / 2 - FLAP_H + 0.008
    pack.group.updateWorldMatrix(true, false)
    this.clipNormal.set(0, 1, 0).transformDirection(pack.group.matrixWorld)
    this.clipPoint.set(0, mouthY, 0).applyMatrix4(pack.group.matrixWorld)
    this.cardClipPlane.setFromNormalAndCoplanarPoint(
      this.clipNormal,
      this.clipPoint,
    )
  }

  /**
   * Detach the crimped top as a cut strip that flies away.
   * Do not rotate it like a box lid on a hinge.
   */
  private poseFlapTear(pack: StackPack, amount: number): void {
    const restY = PACK_H / 2 - FLAP_H
    const nibble = easeOut(clamp01(amount / 0.22))
    const fly = easeOut(clamp01((amount - 0.18) / 0.82))
    pack.flap.visible = fly < 0.97
    const compact = this.frame !== 'wide'
    const flyX = compact ? 0.04 : 0.92
    const flyY = compact ? 1.22 : 1.72
    const flyZ = compact ? 0.28 : 0.88
    pack.flap.position.set(
      lerp(0, 0.14, nibble) + lerp(0, flyX, fly),
      restY + lerp(0, 0.03, nibble) + lerp(0, flyY, fly),
      lerp(0, 0.07, nibble) + lerp(0, flyZ, fly),
    )
    pack.flap.rotation.set(
      lerp(0, 0.4, fly),
      lerp(0, 2.35, fly),
      lerp(0, 0.7, fly),
    )
    pack.flap.scale.setScalar(lerp(1, 0.62, fly))
  }

  private poseHold(pack: StackPack, t: number): void {
    pack.group.position.copy(pack.restPos)
    pack.group.rotation.copy(pack.restEuler)
    pack.group.scale.setScalar(this.holdScale())
    let amount = this.tearAmount
    if (this.holdRipping) {
      const u = easeOut(clamp01((performance.now() - this.ripStartedAt) / 420))
      amount = lerp(this.ripFrom, 1, u)
      this.tearAmount = amount
    }
    this.poseFlapTear(pack, amount)
    this.updateCardClip(pack)
    this.poseCardsAmount(pack, clamp01((amount - 0.38) / 0.62))
    if (amount > 0.55 && !this.openPlayed) {
      this.openPlayed = true
      this.callbacks.onOpen?.()
      this.igniteSparkles(t)
    }
  }

  private tick = (): void => {
    if (this.disposed || this.contextLost) return
    const elapsed = (performance.now() - this.startedAt) / 1000
    const t = this.mode === 'draw' && this.skipped ? this.completeAt : elapsed

    this.placeCamera(Math.min(t, this.completeAt))

    for (const pack of this.packs) {
      if (this.mode === 'draw' && pack.isTop) {
        this.poseTop(pack, t)
        continue
      }
      if (this.mode === 'hold' && pack.isTop) {
        this.poseHold(pack, t)
        continue
      }
      const bob = Math.sin(t * 1.4 + pack.restPos.y * 8) * 0.004
      pack.group.position.y = pack.restPos.y + bob
      pack.group.rotation.y = pack.restEuler.y + Math.sin(t * 0.7) * 0.01
      pack.flap.visible = true
      pack.flap.position.set(0, PACK_H / 2 - FLAP_H, 0)
      pack.flap.rotation.set(0, 0, 0)
      pack.flap.scale.setScalar(1)
    }

    if (this.sparkles.length > 0) this.tickSparkles(t)

    this.renderer.render(this.scene, this.camera)
    if (this.contextLost) return

    if (this.mode === 'draw' && t >= this.completeAt) {
      this.finish()
      return
    }
    this.raf = requestAnimationFrame(this.tick)
  }

  private finish(): void {
    if (this.finished || this.disposed) return
    this.finished = true
    this.renderer.render(this.scene, this.camera)
    this.resolvePlay?.()
    this.resolvePlay = null
  }
}
